import React, { useState } from 'react';
import RichTextEditor from 'legacy/src/components/content/editor/types/shared/RichTextEditor';
import cn from 'classnames';
import makeCursor from 'legacy-src/utility/Cursor';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Icon } from 'legacy/packages/ft-cb';
import Button from 'chameleon/ui-stack/ui/base/button';
import A11y from 'chameleon/ui-stack/utilities/a11y';

import { useScoring } from './ScoringProvider';

import EditScoringPointComponent from './EditScoringPointComponent';

const EditScoringSectionComponent = ({
  sectionIndex,
  sectionTitle,
  scoringInstructions,
  scoringFeedback,
  handleUpdateScoringSectionTitle,
  handleUpdateScoringSectionInstructions,
  handleRemoveScoringSection,
  handleMoveScoringSectionUp,
  handleMoveScoringSectionDown,
  showActionButtons,
  showValidation,
  innerErrors,
}) => {
  const dispatch = useScoring()[1];
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const handleAddPoint = () => {
    dispatch({ type: 'addPoint', payload: { sectionIndex } });
  };

  const handleUpdateScoringFeedback = (feedback, index) => {
    dispatch({
      type: 'updatePointScoringFeedback',
      payload: { feedback, index, sectionIndex },
    });
  };

  const handleUpdateScoringAdditionalFeedback = (
    additional_feedback,
    index,
  ) => {
    dispatch({
      type: 'updatePointScoringAdditionalFeedback',
      payload: { additional_feedback, index, sectionIndex },
    });
  };

  const handleRemovePoints = (index) => {
    dispatch({ type: 'removePoint', payload: { sectionIndex, index } });
  };

  const scoringInstructionsCursor = makeCursor(
    scoringInstructions,
    [],
    (data) => handleUpdateScoringSectionInstructions(data, sectionIndex),
  );

  const generateErrorClass = (value) =>
    showValidation && !value ? 'error' : '';

  const getValidationMessage = (path) => {
    const error =
      innerErrors.find((innerError) => innerError.path === path) || {};
    return error.message;
  };

  const instructionsValidation =
    showValidation &&
    getValidationMessage(
      `[${sectionIndex}].scoringInstructions.content[0].content`,
    );

  return (
    <div className="ScoreCategoryEditor__category">
      <div className="ScoreCategoryEditor__section_header">
        <h3 className="font-heading font-bold text-22 mt-0">
          Scoring Section
          {showActionButtons && (
            <span>
              <A11y>
                <span
                  aria-label="Move section up"
                  role="button"
                  tabIndex={0}
                  className="ScoreCategoryEditor-move-section-up"
                  onClick={() => handleMoveScoringSectionUp(sectionIndex)}
                >
                  <div className="section_header-icon_container --round">
                    <Icon
                      data-test-move-section-up-icon
                      name="chevron_single_ascending"
                      size="2rem"
                    />
                  </div>
                </span>
              </A11y>
              <A11y>
                <span
                  aria-label="Move section down"
                  role="button"
                  tabIndex={0}
                  className="ScoreCategoryEditor-move-section-down"
                  onClick={() => handleMoveScoringSectionDown(sectionIndex)}
                >
                  <div className="section_header-icon_container --round">
                    <Icon
                      data-test-move-section-down-icon
                      name="chevron_single_descending"
                      size="2rem"
                    />
                  </div>
                </span>
              </A11y>
            </span>
          )}
        </h3>
        {showActionButtons && (
          <A11y>
            <span
              aria-label="Delete Section"
              role="button"
              tabIndex={0}
              className="ScoreCategoryEditor-remove-score-section"
              onClick={() => setShowConfirmationDialog(true)}
            >
              <div className="section_header-icon_container --square">
                <Icon data-test-remove-section-icon name="trash" size="2rem" />
                <span>Delete Section</span>
              </div>
            </span>
          </A11y>
        )}
        {showConfirmationDialog && (
          <ConfirmDialog
            title="Confirm"
            onClose={() => setShowConfirmationDialog(false)}
            onSuccess={() => {
              handleRemoveScoringSection(sectionIndex);
              setShowConfirmationDialog(false);
            }}
            aria={{
              live: 'polite',
            }}
            content={{
              btnSecondary: 'No',
              btnPrimary: 'Yes',
            }}
          >
            <p id="delete-scoring-section-warning">
              Are you sure you want to delete this scoring section?
              <br />
              This action cannot be undone and your changes will be lost.
            </p>
          </ConfirmDialog>
        )}
      </div>
      <div className="ScoreCategoryEditor__header_container">
        <h4 className="label-column text-base text-gray-900 font-normal mb-4">
          Section title
        </h4>
        <input
          className={`border border-gray-300 rounded-full p-4 h-full text-14 focus:border-gray-900${generateErrorClass(
            sectionTitle,
          )} `}
          onChange={(e) =>
            handleUpdateScoringSectionTitle(e.target.value, sectionIndex)
          }
          value={sectionTitle}
          style={{ width: '100%' }}
          placeholder="Add section title..."
          type="text"
        />
        {showValidation && !sectionTitle && (
          <div className="action-column error-message --offset">
            {getValidationMessage(`[${sectionIndex}].sectionTitle`)}
          </div>
        )}
      </div>
      <div className="ScoreCategoryEditor__header_container">
        <h4 className="label-column text-base text-gray-900 font-normal mb-4">
          Scoring Instructions
        </h4>
        <div className="">
          <RichTextEditor
            hasError={instructionsValidation}
            valueLink={scoringInstructionsCursor}
            placeholder="Scoring Instructions"
          />
        </div>
        {instructionsValidation && (
          <div className="action-column error-message --offset">
            {instructionsValidation}
          </div>
        )}
      </div>
      <div className="ScoreCategoryEditor__points mb-10">
        {scoringFeedback.map((point, index) => {
          const feedbackCursor = makeCursor(point.feedback, [], (data) =>
            handleUpdateScoringFeedback(data, index),
          );
          const additionalFeedbackCursor = makeCursor(
            point.additional_feedback,
            [],
            (data) => handleUpdateScoringAdditionalFeedback(data, index),
          );

          const feedbackValidation =
            showValidation &&
            getValidationMessage(
              `[${sectionIndex}].scoringFeedback[${index}].feedback.content[0].content`,
            );

          const getCriteriaValidation = (criteriaIndex) =>
            showValidation &&
            getValidationMessage(
              `[${sectionIndex}].scoringFeedback[${index}].criterias[${criteriaIndex}].description.content[0].content`,
            );

          return (
            <EditScoringPointComponent
              key={`${index}_scoring_feedback`}
              sectionIndex={sectionIndex}
              pointIndex={index}
              getValidationMessage={getValidationMessage}
              feedbackCursor={feedbackCursor}
              feedbackValidation={feedbackValidation}
              getCriteriaValidation={getCriteriaValidation}
              criterias={point.criterias}
              additionalFeedbackCursor={additionalFeedbackCursor}
              handleRemovePoints={handleRemovePoints}
            />
          );
        })}
        <Button
          isPrimary
          className={cn(
            'ScoreCategoryEditor__add_point mt-10 text-center w-full',
          )}
          onClick={handleAddPoint}
        >
          + Add Point
        </Button>
      </div>
    </div>
  );
};

EditScoringSectionComponent.propTypes = {
  // TODO: Add this back somehow
  // isItemLoaded: PropTypes.bool.isRequired,
};

export default EditScoringSectionComponent;
